import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Sparkles } from 'lucide-react';
import ModalWrapper, { ModalCard } from '../../components/ModalWrapper';
import BottomNotice from '../../components/ui/BottomNotice';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * SimilarAfterAdd — "is it the same as one of these?", asked after the node
 * is already on the map. A notification at the bottom first; the list only
 * when asked for. A pick draws an *on sama* link between the two nodes and
 * deletes nothing. Successor to the pre-add "Did you mean?" step for v2.
 */

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 28px 30px 30px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.4rem;
  color: #8ec1f0;
`;

const Muted = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
`;

const Added = styled.div`
  border-left: 3px solid #5B9DD9;
  padding: 0.5rem 0 0.5rem 0.9rem;
  font-weight: 600;
`;

const Card = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  &:hover { border-color: #5B9DD9; background: rgba(91, 157, 217, 0.08); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const CardTitle = styled.div`
  font-weight: 700;
`;

const Badge = styled.span`
  align-self: flex-start;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: rgba(76, 175, 80, 0.16);
  color: #8fe0a8;
`;

const Close = styled.button`
  all: unset;
  cursor: pointer;
  align-self: flex-start;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  font-weight: 600;
`;

export function SimilarAfterAddToast({ findings, onShow, onDismiss }) {
  const { t } = useTranslation();
  if (!findings) return null;
  return (
    <BottomNotice
      tone="info"
      icon={<Sparkles size={18} />}
      message={t('impactPath.similarAfterAdd.found', { count: findings.existingNodes.length, title: findings.title })}
      primaryLabel={t('impactPath.similarAfterAdd.show')}
      onPrimary={onShow}
      secondaryLabel={t('impactPath.similarAfterAdd.dismiss')}
      onSecondary={onDismiss}
    />
  );
}

export default function SimilarAfterAddModal({ findings, onSame, onClose }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  if (!findings) return null;
  return (
    <ModalWrapper visible onClose={onClose} zIndex={1000} opacity={0.85}>
      <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
        <Body>
          <Title>{t('impactPath.similarAfterAdd.title')}</Title>
          <Muted>{t('impactPath.similarAfterAdd.intro')}</Muted>
          <Added>{findings.title}</Added>
          {findings.existingNodes.map((node) => (
            <Card key={node.id} disabled={busy} onClick={async () => { setBusy(true); try { await onSame(node); } finally { setBusy(false); } }}>
              <CardTitle>{node.text || node.title}</CardTitle>
              {node.description && node.description !== (node.text || node.title) && <Muted>{node.description}</Muted>}
              {node.similarity != null && (
                <Badge>{t('impactPath.similarAfterAdd.similarity', { percent: Math.round(node.similarity * 100) })}</Badge>
              )}
            </Card>
          ))}
          <Close type="button" onClick={onClose}>{t('impactPath.similarAfterAdd.notSame')}</Close>
        </Body>
      </ModalCard>
    </ModalWrapper>
  );
}
